<script setup lang="ts">
/**
 * The canvas the hangar renders into, and the thing that owns its lifetime.
 *
 * Deliberately thin — it decides *when* a scene exists and nothing about what it
 * looks like. All of that is `hangar-scene.ts`, including the reasoning about
 * the WebGL context budget, which is the constraint the whole feature is shaped
 * around.
 *
 * Two responsibilities worth stating out loud:
 *
 *   **It disposes on unmount, synchronously.** The parent mounts it with `v-if`,
 *   so leaving the garage tab or deploying tears the context down before the
 *   arena asks for one. Anything looser and the arena can come up black on a
 *   machine short of contexts.
 *
 *   **It degrades rather than failing.** Babylon is imported dynamically, the
 *   scene is only built if previews are allowed, and every failure path lands on
 *   the still preview the lobby used before. A player who turned previews off
 *   for performance has already told us not to do this.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import ItemPreview from '@/garage/ItemPreview.vue';
import { hangarRoomEnabled, previewsAllowed, previewsEnabled } from '@/state/settings';
import type { HangarQuality, HangarScene } from './hangar-scene';

const props = defineProps<{
  hullId: string | null;
  turretId: string | null;
  paintId?: string | null;
  label: string;
  /** Lower tier drops the shadow and the backdrop. See `HangarQuality`. */
  quality?: HangarQuality;
  /**
   * True while another tab sits on top of the scene. The hangar stays alive
   * behind every garage tab — that is the point of it being mounted once by
   * `GarageScreen` — but a backgrounded room gives up its pointer, its
   * foreground pixel density and most of its frames. See
   * `HangarScene.setBackgrounded`.
   */
  background?: boolean;
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
/** True once a scene is up and drawing. Until then the still stands in. */
const live = ref(false);

/**
 * Whether the still is worth rendering at all.
 *
 * `ItemPreview` gates itself on the same setting, so with previews off it sits
 * in its pending state forever — and the fallback for a scene that could not be
 * built was therefore *nothing*, a blank rectangle the size of a hangar with the
 * In-battle button underneath it. The button was still reachable, which is the
 * rule that actually matters, but "never a blank area where the tank was" is a
 * rule too. Below this is a plain plate that names the vehicle: no renderer, no
 * canvas, no WebGL context, and it says what you are about to deploy in.
 */
const stillPossible = computed(() => previewsEnabled.value);

let scene: HangarScene | null = null;

async function build(): Promise<void> {
  // `hangarRoomEnabled` is the room's own dial — see `state/settings.ts` for
  // why it is not folded into `previews`. Off means the still plate below,
  // which is the same complete fallback a refused WebGL context lands on.
  if (!hangarRoomEnabled.value || !previewsAllowed() || !canvas.value || scene) return;
  try {
    // Imported here rather than at the top so Babylon stays off the garage's
    // critical path — the same argument `ItemPreview` makes, and the reason the
    // shop paints before any of the renderer is fetched.
    const { HangarScene } = await import('./hangar-scene');
    if (!canvas.value) return;
    scene = new HangarScene(canvas.value, props.quality ?? 'high');
    scene.loadBackdrop();
    scene.setBackgrounded(Boolean(props.background));
    scene.start();
    await scene.setLoadout({ hullId: props.hullId, turretId: props.turretId, paintId: props.paintId });
    // Only now: a canvas revealed before the meshes land shows an empty room,
    // which reads worse than the still it replaced.
    if (scene) live.value = true;
  } catch {
    // No WebGL, a chunk that 404s, a context refused. The still is a complete
    // fallback, so this costs the room and nothing else.
    teardown();
  }
}

function teardown(): void {
  scene?.dispose();
  scene = null;
  live.value = false;
}

onMounted(build);
onBeforeUnmount(teardown);

// Toggled from the settings window, which is open *over* the garage — so the
// room has to come down and go back up without a remount. Tearing down
// disposes the WebGL context, which is the whole point of the switch.
watch(hangarRoomEnabled, (wanted) => {
  if (wanted) void build();
  else teardown();
});

// Swapping a bay changes the tank in the room rather than rebuilding the room.
watch(
  () => [props.hullId, props.turretId, props.paintId],
  () => {
    void scene?.setLoadout({ hullId: props.hullId, turretId: props.turretId, paintId: props.paintId });
  },
);

// Turning previews off mid-session has to take the context with it, or the
// setting is a label rather than a control.
watch(previewsAllowed, (allowed) => {
  if (allowed) void build();
  else teardown();
});

watch(
  () => props.background,
  (background) => scene?.setBackgrounded(Boolean(background)),
);

defineExpose({
  /** For the dev probe. Null when the room never built. */
  report: () => scene?.report() ?? null,
  resetView: () => scene?.resetView(),
  /** For live focus tuning from the dev probe. See `HangarScene.setFocus`. */
  setFocus: (fogStart: number, fogEnd: number, spotIntensity: number, fogGrey?: number) =>
    scene?.setFocus(fogStart, fogEnd, spotIntensity, fogGrey),
});
</script>

<template>
  <div class="hangar" data-test="hangar">
    <canvas ref="canvas" :class="{ live }" :aria-label="props.label"></canvas>

    <div v-if="!live && stillPossible" class="still">
      <ItemPreview
        :hull-id="props.hullId"
        :turret-id="props.turretId"
        framing="tank"
        :label="props.label"
      />
    </div>

    <div v-else-if="!live" class="plate" data-test="hangar-plate">
      <span class="plate-label">In the bay</span>
      <span class="plate-name">{{ props.label }}</span>
      <span class="plate-hint">Previews are off in settings</span>
    </div>

    <p v-if="live && !props.background" class="hint" aria-hidden="true">Drag to look around</p>
  </div>
</template>

<style scoped>
.hangar {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 0;
  overflow: hidden;
  border-radius: 4px;
  background: #0b0e12;
}

canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  /* Hidden rather than absent until the tank has loaded: the element has to
     exist for the engine to attach to, but an empty lit room is a worse first
     impression than the still. */
  opacity: 0;
  transition: opacity 220ms ease;
  /* The drag is an orbit, so the browser must not also select or scroll. */
  touch-action: none;
}

canvas.live {
  opacity: 1;
  cursor: grab;
}

canvas.live:active {
  cursor: grabbing;
}

.still {
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  padding: 0.5rem;
}

.plate {
  display: grid;
  place-items: center;
  gap: 0.35rem;
  padding: 2rem;
  text-align: center;
}

.plate-label {
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.plate-name {
  font-size: 1.3rem;
  font-weight: 600;
}

.plate-hint {
  font-size: 0.72rem;
  color: var(--steel-600);
}

.still :deep(.preview) {
  height: 100%;
  width: auto;
  max-width: 100%;
  border: none;
  background: none;
}

/*
 * Bottom centre, not bottom right.
 *
 * The corners of this scene belong to the lobby's own controls — the In-battle
 * button is bottom right and the bays are bottom left — and the hint spent its
 * first render underneath the button. The middle of the bottom edge is the one
 * strip nothing else claims, which is the same reason the tank is in the middle.
 */
.hint {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0.55rem;
  margin: 0;
  font-size: 0.66rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(242, 244, 247, 0.32);
  pointer-events: none;
}
</style>
